import { ButtonBase, Zoom, Typography, Hidden, Modal } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import { useEffect, useRef, useState } from 'react'

import { getDirection, getTranslate } from '../../localization'
import InventoryCheckboxItem from './InventoryCheckboxItem'
import InventoryInputItem from './InventoryInputItem'
import InventoryRestrictionSelector, {
  BOOLEAN_RESTRICTIONS_LIST,
  INPUT_RESTRICTIONS_LIST,
  Restrictions
} from './InventoryRestrictionSelector'
import useStyles from './styles/InventoryRestrictions.styles'

interface InventoryRestrictionsProps {
  activeRestrictions: Record<
    Restrictions,
    { active: boolean; value?: boolean | string }
  >
  setActiveRestrictions: React.Dispatch<
    React.SetStateAction<
      Record<
        Restrictions,
        {
          active: boolean
          value?: string | boolean | undefined
        }
      >
    >
  >
}

const InventoryRestrictions: React.FC<InventoryRestrictionsProps> = ({
  activeRestrictions,
  setActiveRestrictions
}) => {
  const classes = useStyles()
  const [isOpen, setisOpen] = useState(false)
  const restrictionSelectorRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      if (
        restrictionSelectorRef.current &&
        !restrictionSelectorRef?.current?.contains(event.target as Node) &&
        isOpen
      ) {
        setTimeout(() => {
          setisOpen(false)
        }, 260)
      }
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [restrictionSelectorRef, isOpen])

  return (
    <div className={classes.root}>
      <div className={classes.restrictionSelector}>
        <div className={classes.titleContainer}>
          {getTranslate('Restrictions')}
        </div>
        <ButtonBase
          className={[
            classes.valueContainer,
            classes.restrictionSelectorTitle
          ].join(' ')}
          onClick={() => setisOpen(!isOpen)}>
          <AddIcon color="primary" />
          <Typography>{getTranslate('Add Restrictions')}</Typography>
        </ButtonBase>
        <Hidden smDown>
          <Zoom in={isOpen}>
            <div
              ref={restrictionSelectorRef}
              className={classes.inventoryRestrictionSelectorContainer}>
              <InventoryRestrictionSelector
                {...{ activeRestrictions, setActiveRestrictions }}
              />
            </div>
          </Zoom>
        </Hidden>
        <Hidden mdUp>
          <Modal
            dir={getDirection()}
            open={isOpen}
            className={classes.inventoryRestrictionSelectorContainerMobile}>
            <div
              ref={restrictionSelectorRef}
              className={classes.inventoryRestrictionSelectorContainerMobile}>
              <InventoryRestrictionSelector
                {...{ activeRestrictions, setActiveRestrictions }}
              />
            </div>
          </Modal>
        </Hidden>
      </div>
      {BOOLEAN_RESTRICTIONS_LIST.filter(
        (item) => activeRestrictions[item.value].active
      ).map((booleanRestrictionItem) => (
        <InventoryCheckboxItem
          label={booleanRestrictionItem.label}
          checked={
            (activeRestrictions[booleanRestrictionItem.value]
              .value as boolean) || false
          }
          onToggle={() =>
            setActiveRestrictions({
              ...activeRestrictions,
              [booleanRestrictionItem.value]: {
                ...activeRestrictions[booleanRestrictionItem.value],
                value: !activeRestrictions[booleanRestrictionItem.value].value
              }
            })
          }
          onDelete={() =>
            setActiveRestrictions(() => ({
              ...activeRestrictions,
              [booleanRestrictionItem.value]: {
                ...activeRestrictions[booleanRestrictionItem.value],
                active: !activeRestrictions[booleanRestrictionItem.value].active
              }
            }))
          }
        />
      ))}
      {INPUT_RESTRICTIONS_LIST.filter(
        (item) => activeRestrictions[item.value].active
      ).map((booleanRestrictionItem) => (
        <InventoryInputItem
          deletable
          label={booleanRestrictionItem.label}
          value={
            (activeRestrictions[booleanRestrictionItem.value]
              .value as string) || ''
          }
          onChange={(value) =>
            setActiveRestrictions({
              ...activeRestrictions,
              [booleanRestrictionItem.value]: {
                ...activeRestrictions[booleanRestrictionItem.value],
                value
              }
            })
          }
          onDelete={() =>
            setActiveRestrictions(() => ({
              ...activeRestrictions,
              [booleanRestrictionItem.value]: {
                ...activeRestrictions[booleanRestrictionItem.value],
                active: !activeRestrictions[booleanRestrictionItem.value].active
              }
            }))
          }
        />
      ))}
    </div>
  )
}

export default InventoryRestrictions
